import type React from 'react';
import type { ChangeEvent, DragEvent } from 'react';
import { useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { zodResolver } from '@hookform/resolvers/zod';
import { useLocation } from '@tanstack/react-router';
import { FileTextIcon, XIcon } from 'lucide-react';
import type { ZodType } from 'zod';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import Spinner from '@/components/ui/spinner';
import { Modal } from '@/components/modals/new/modal';
import type { useModal } from '@/components/modals/new/use-modal';
import { env } from '@/constants/env';
import { cn } from '@/lib/utils';
import { parseStringToBool } from '@/utils/parseStringToBool';

import { ACCEPTED_MEDIA_TYPES } from '../schema/upload-media';

export type UploadMediaModalType = {
  files: File[];
  fileName: string;
  redirect?: boolean;
};

const DEFAULT_UPLOAD_MEDIA_VALUES: UploadMediaModalType = {
  files: [],
  fileName: '',
  redirect: false,
};

export interface UploadMediaModalProps {
  submitHandler: (data: UploadMediaModalType) => Promise<void>;
  acceptedType: string;
  acceptedMediaSchema: ZodType;
  modal: ReturnType<typeof useModal>;
}

const getFileKey = (file: File) => `${file.name}-${file.size}-${file.lastModified}-${file.type}`;

export const UploadMediaModal: React.FC<UploadMediaModalProps> = ({
  submitHandler,
  acceptedMediaSchema,
  acceptedType,
  modal,
}) => {
  const { t } = useTranslation();
  const { pathname } = useLocation();

  const {
    register,
    handleSubmit,
    watch,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<UploadMediaModalType>({
    resolver: zodResolver(acceptedMediaSchema),
    defaultValues: DEFAULT_UPLOAD_MEDIA_VALUES,
  });

  const files = watch('files');
  const isFloorPlansPage = pathname.includes('floor-plans');
  const canCreate3DModel = isFloorPlansPage && parseStringToBool(env.SHOW_PAPER_2_PIXEL);

  const addFiles = (newFiles: File[]) => {
    reset({
      ...DEFAULT_UPLOAD_MEDIA_VALUES,
      files: [...files, ...newFiles],
    });
  };

  const fileInputChangeHandler = (event: ChangeEvent<HTMLInputElement>) => {
    const newFiles = Array.from(event.target.files ?? []);
    addFiles(newFiles);
  };

  const handleDragOver = (event: DragEvent<HTMLLabelElement>) => {
    event.preventDefault();
  };

  const handleDrop = (event: DragEvent<HTMLLabelElement>) => {
    event.preventDefault();

    const newFiles = Array.from(event.dataTransfer.files ?? []);
    addFiles(newFiles);
  };

  const removeFile = (indexToRemove: number) => {
    const updatedFiles = files.filter((_, index) => index !== indexToRemove);

    reset({
      ...DEFAULT_UPLOAD_MEDIA_VALUES,
      files: updatedFiles,
    });
  };

  const submitAndReset = handleSubmit(async (data) => {
    await submitHandler(data);
    reset(DEFAULT_UPLOAD_MEDIA_VALUES);
  });

  const saveAndCreate3DModel = handleSubmit(async (data) => {
    await submitHandler({
      ...data,
      redirect: true,
    });

    reset(DEFAULT_UPLOAD_MEDIA_VALUES);
  });

  return (
    <Modal
      {...modal}
      title=""
      className={cn(
        'max-h-[90vh] w-[calc(100vw-2rem)] max-w-96 overflow-y-auto',
        isFloorPlansPage && 'sm:max-w-[34rem]'
      )}
    >
      {isSubmitting ? (
        <output
          className="absolute inset-0 z-10 flex items-center justify-center bg-primary-lighter/40"
          aria-label={t('loading')}
        >
          <Spinner className="size-12 [&_*]:stroke-primary-dark" aria-hidden="true" />
        </output>
      ) : null}

      <form onSubmit={submitAndReset}>
        <Modal.Body>
          <div className="flex w-full flex-col items-center justify-center space-y-3">
            <label
              htmlFor="dropzone-file"
              className="my-4 flex h-64 w-full cursor-pointer flex-col items-center justify-center rounded-lg border-2 border-dashed border-text-secondary p-4 hover:bg-secondary"
              onDragOver={handleDragOver}
              onDrop={handleDrop}
            >
              <div className="flex flex-col items-center justify-center pb-6 pt-5">
                <svg
                  className="mb-4 size-8 text-text-secondary"
                  aria-hidden="true"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 20 16"
                >
                  <path
                    stroke="currentColor"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M13 13h3a3 3 0 0 0 0-6h-.025A5.56 5.56 0 0 0 16 6.5 5.5 5.5 0 0 0 5.207 5.021C5.137 5.017 5.071 5 5 5a4 4 0 0 0 0 8h2.167M10 15V6m0 0L8 8m2-2 2 2"
                  />
                </svg>

                <p className="mb-1 text-center text-sm text-text-secondary">
                  {t('uploadDocumentText')}
                </p>

                <p className={cn('text-sm text-text-secondary', errors.files && 'text-danger')}>
                  {acceptedType}
                </p>
              </div>

              <Input
                id="dropzone-file"
                type="file"
                accept={acceptedType}
                className="hidden"
                disabled={isSubmitting}
                multiple
                {...register('files', {
                  onChange: fileInputChangeHandler,
                })}
              />
            </label>
          </div>

          {files.length > 0 ? (
            <div className="grid max-h-96 grid-cols-3 gap-2 overflow-y-scroll">
              {files.map((file, index) => (
                <div key={`${getFileKey(file)}-${index}`} className="relative h-28">
                  <UploadMediaPreview file={file} />

                  <button
                    type="button"
                    className="absolute right-0 top-0 rounded-full text-text-secondary"
                    onClick={() => removeFile(index)}
                    disabled={isSubmitting}
                    aria-label={`${t('remove')} ${file.name}`}
                  >
                    <XIcon aria-hidden className="size-4" />
                  </button>
                </div>
              ))}
            </div>
          ) : null}
        </Modal.Body>

        <Modal.Actions className="block space-y-4 sm:block sm:space-x-0 sm:space-y-4">
          {files.length > 0 ? (
            <div className="flex justify-end">
              <Button
                type="button"
                variant="outline"
                color="primary"
                className="bg-white px-5"
                disabled={isSubmitting}
                onClick={() => reset(DEFAULT_UPLOAD_MEDIA_VALUES)}
              >
                {t('clearAll')}
              </Button>
            </div>
          ) : null}

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Button
              type="button"
              variant="outline"
              className="border-red-500 text-red-500"
              disabled={isSubmitting}
              onClick={modal.dismiss}
            >
              {t('cancel')}
            </Button>

            <Button type="submit" disabled={isSubmitting}>
              {t('submit')}
            </Button>

            {canCreate3DModel ? (
              <Button
                type="button"
                className="min-w-0 px-4 text-center text-sm sm:col-span-2"
                disabled={isSubmitting}
                onClick={saveAndCreate3DModel}
              >
                {t('saveAndCreate3DModel')}
              </Button>
            ) : null}
          </div>
        </Modal.Actions>
      </form>
    </Modal>
  );
};

type UploadMediaPreviewProps = {
  file: File;
};

const UploadMediaPreview: React.FC<UploadMediaPreviewProps> = ({ file }) => {
  const objectUrl = useMemo(() => URL.createObjectURL(file), [file]);

  useEffect(
    () => () => {
      URL.revokeObjectURL(objectUrl);
    },
    [objectUrl]
  );

  if (file.type === ACCEPTED_MEDIA_TYPES.Pdf) {
    return (
      <div className="flex size-full items-center justify-center text-primary-light">
        <FileTextIcon size={80} />
      </div>
    );
  }

  if (file.type.startsWith('video/')) {
    return <video src={objectUrl} className="size-full rounded object-cover" muted playsInline />;
  }

  return (
    <img src={objectUrl} alt={`Preview ${file.name}`} className="size-full rounded object-cover" />
  );
};
